import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const GROUPS = ['entitlement', 'workshop', 'managed'];

/**
 * vadmin "Ominaisuudet" tab: the staff-editable feature flags of one organization
 * (vadmin/feature_flags.py). Switches edit local state; nothing is sent until Tallenna,
 * which PATCHes only the changed fields. Read-only ("managed") flags are shown disabled.
 */
export default function FeatureFlagsPanel({ organizationId, onChanged }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const url = `/api/vadmin/organizations/${organizationId}/feature-flags/`;

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(url);
      setData(res.data);
      setDraft({});
    } catch {
      setError(t('vadmin.flagsLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  if (loading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!data) {
    return <Alert severity="error">{error || t('vadmin.flagsLoadFailed')}</Alert>;
  }

  const currentValue = (name) => (name in draft ? draft[name] : data.flags.find((f) => f.name === name)?.value);
  const currentLookups = 'extra_vehicle_lookups' in draft ? draft.extra_vehicle_lookups : data.extra_vehicle_lookups;
  const dirtyCount = Object.keys(draft).length;

  const toggle = (name, value) => {
    setSaved(false);
    setDraft((prev) => {
      const next = { ...prev };
      const original = data.flags.find((f) => f.name === name)?.value;
      if (value === original) delete next[name];
      else next[name] = value;
      return next;
    });
  };

  const setLookups = (raw) => {
    setSaved(false);
    const value = raw === '' ? 0 : parseInt(raw, 10);
    setDraft((prev) => {
      const next = { ...prev };
      if (Number.isNaN(value) || value === data.extra_vehicle_lookups) delete next.extra_vehicle_lookups;
      else next.extra_vehicle_lookups = value;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await axios.patch(url, draft);
      setData(res.data);
      setDraft({});
      setSaved(true);
      if (onChanged) await onChanged();
    } catch (err) {
      const body = err.response?.data;
      if (body?.code === 'requires_entitlement') {
        setError(t('vadmin.flagsNeedsEntitlement', { flag: t(`vadmin.flags.${body.field}`) }));
      } else {
        setError(t('vadmin.flagsSaveFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box data-testid="feature-flags-panel">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)} data-testid="feature-flags-error">
          {error}
        </Alert>
      )}
      {saved && !dirtyCount && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)} data-testid="feature-flags-saved">
          {t('vadmin.flagsSaved')}
        </Alert>
      )}

      {GROUPS.map((group) => {
        const rows = data.flags.filter((f) => f.group === group);
        if (!rows.length) return null;
        return (
          <Paper key={group} variant="outlined" sx={{ p: 2, mb: 2 }} data-testid={`feature-flags-group-${group}`}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t(`vadmin.flagGroup.${group}`)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t(`vadmin.flagGroupHint.${group}`)}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {rows.map((flag) => {
                const value = currentValue(flag.name);
                const dirty = flag.name in draft;
                return (
                  <Box
                    key={flag.name}
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 0 },
                    }}
                  >
                    <FormControlLabel
                      sx={{ flex: '1 1 260px', m: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(value)}
                          disabled={!flag.editable || saving}
                          onChange={(e) => toggle(flag.name, e.target.checked)}
                          data-testid={`feature-flag-${flag.name}`}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: dirty ? 700 : 500 }}>
                            {t(`vadmin.flags.${flag.name}`)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {flag.name}
                          </Typography>
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {flag.plan_granted && (
                        <Chip size="small" color="info" variant="outlined" label={t('vadmin.flagPlanGranted')} />
                      )}
                      {!flag.editable && (
                        <Chip size="small" variant="outlined" label={t('vadmin.flagManagedElsewhere')} />
                      )}
                      {dirty && <Chip size="small" color="warning" label={t('vadmin.flagUnsaved')} />}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        );
      })}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {t('vadmin.extraVehicleLookups')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('vadmin.extraVehicleLookupsHint')}
        </Typography>
        <TextField
          size="small"
          type="number"
          inputProps={{ min: 0, step: 1, 'data-testid': 'feature-flags-extra-lookups' }}
          value={currentLookups}
          onChange={(e) => setLookups(e.target.value)}
          sx={{ width: 160 }}
        />
      </Paper>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button variant="outlined" disabled={!dirtyCount || saving} onClick={() => { setDraft({}); setError(null); }}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!dirtyCount || saving}
          onClick={save}
          data-testid="feature-flags-save"
        >
          {saving ? t('vadmin.flagsSaving') : t('vadmin.flagsSave', { count: dirtyCount })}
        </Button>
      </Box>
    </Box>
  );
}
